import { Fragment, useState } from 'react'
import { Box, Card, CardContent, Grid, Typography, useTheme } from '@mui/material'
import { Device, Port } from '../types/models'
import { CustomSwitch } from '../components/atoms/CustomSwitch'
import { Safety } from '../classes/Safety'
import { VERSION } from '../version'
import { useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'

function DebugToggle(props: { name: string, title: string }): React.ReactElement {

  const debug: Record<string, unknown> = Safety.get('debug', {})
  const { name, title } = props
  const [toggle, setToggle] = useState(!!debug[name])

  return <Box sx={{ p: 0.5 }}>
    <CustomSwitch
      checked={toggle}
      onClick={() => {
        setToggle(!toggle)
        Safety.set('debug', { ...Safety.get('debug', {}), [name]: !debug[name] })
      }}
    />
    &nbsp;&nbsp;&nbsp;
    {title}
  </Box>
}

function DebugToggles(): React.ReactElement {
  return <>
    <DebugToggle name="socket" title="Show WebSocket Traffic"/>
    <DebugToggle name="port-json" title="Show JSON config and values for port"/>
    <DebugToggle name="port-numbers" title="Show device IDs and port numbers"/>
    <DebugToggle name="rule-json" title="Show JSON for rules in editor"/>
    <DebugToggle name="high-accuracy" title="Add more decimals to statistics"/>
    <DebugToggle name="config-diff" title="View exact details of configuration differences in device list"/>
    <DebugToggle name="flashing" title="Show data what is happeninging when saving configuration"/>
    <DebugToggle name="log-pending" title="Print to the console the settings caused pending dialog"/>
    <DebugToggle name="relay-tracking" title="Comprehensive debugging for relay data"/>
    <DebugToggle name="led-tracking" title="Comprehensive debugging for simple led reader data"/>
    <DebugToggle name="always-installer" title="Keep installer mode on"/>
  </>
}

function DebugPort(props: {port: Port}): React.ReactElement {
  const port = { ...props.port }

  return <>
    <Grid item>
      <Card>
        <CardContent>
          <Typography variant="h2">port: {port.port_number}</Typography>
          <pre>{JSON.stringify(port, null, 2)}</pre>
        </CardContent>
      </Card>
    </Grid>
  </>
}

function DebugDevice(props: {device: Device}): React.ReactElement {
  const device = { ...props.device, ports: undefined }

  return <>
    <Grid item>
      <Card>
        <CardContent>
          <Typography variant="h2">Device: {device.name || device.hw_id}</Typography>
          <pre>{JSON.stringify(device, null, 2)}</pre>
        </CardContent>
      </Card>
    </Grid>

    {props.device.ports?.map((port: Port) => <DebugPort key={port.id} port={port}/>)}
  </>
}

export function DebugPage(): React.ReactElement {

  const theme = useTheme()
  const locations = Safety.get('locations', null)
  const devices = Safety.get('devices', [])
  const user = Safety.get('user', null)
  const version = Safety.version()
  const { t } = useTranslation()

  return (
    <Fragment>
      <TopTitle title={t('Debug')}/>

      <Box sx={{ m: 2, p: 2, backgroundColor: theme.palette.debugBackground.main }}>
        <DebugToggles/>
      </Box>

      <Grid container spacing={2}>

        <Grid item>
          <Card>
            <CardContent>
              <Typography variant="h2">Version</Typography>
              Current:
              <pre>{JSON.stringify(VERSION)}</pre>
              Stored:
              <pre>{JSON.stringify(version)}</pre>
            </CardContent>
          </Card>
        </Grid>

        <Grid item>
          <Card>
            <CardContent>
              <Typography variant="h2">Locations</Typography>
              <pre>{JSON.stringify(locations, null, 2)}</pre>
            </CardContent>
          </Card>
        </Grid>

        <Grid item>
          <Card>
            <CardContent>
              <Typography variant="h2">User</Typography>
              <pre>{JSON.stringify(user, null, 2)}</pre>
            </CardContent>
          </Card>
        </Grid>

        {devices.map((device: Device) => <DebugDevice key={device.id} device={device}/>)}

      </Grid>
    </Fragment>
  )
}
